import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./hourlyReports.css";

const HourlyReport = () => {
  const [hourlyReports, setHourlyReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const toastShownRef = useRef(false);

  const fetchHourlyReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}hourlyReport/hourlyreport?limit=${limit}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const data = response.data;
      setHourlyReports(data.data);
      setPageCount(data.totalPages);
      setTotal(data.total);
      if (!toastShownRef.current) {
        showToast.success("Hourly reports fetched successfully");
        toastShownRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching hourly report:", error);
      if (!toastShownRef.current) {
        showToast.error("Failed to fetch hourly reports.");
        toastShownRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHourlyReports();
  }, [page]);

  const handleShare = async (id) => {
    try {
      setLoading(true);
      if (!token) {
        alert("Token not found");
        return;
      }
      const response = await axios.get(
        `${API_URL}hourlyReport/share-report/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        showToast.success(
          response.data.message || "Report shared successfully.",
          "success"
        );
      } else {
        showToast(response.data.message || "Failed to share report.", "error");
      }
    } catch (error) {
      console.error("Error sharing report:", error);
      alert("Failed to share the report.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const formatMarketReport = (report) => {
    const paragraph = report || "";
    const lines = paragraph.trim().split("\n\n");
    const firstLine = lines.shift() || "";
    const headline = lines.shift() || "";

    let formattedText = `<strong>Markets</strong> ${firstLine} ${headline}:`;
    formattedText += `<ul style="margin-top: 10px; padding-left: 20px;">`;
    lines.forEach((line) => {
      formattedText += `<li style="list-style: disc; margin-bottom: 5px;">${line.trim()}</li>`;
    });
    formattedText += "</ul>";

    return formattedText;
  };

  return (
    <div
      className="page-header-fixed page-sidebar-fixed mt-5"
      style={{ overflowX: "hidden" }}
    >
      <div className="panel panel-default with-rounded-corner with-shadow p-10">
        {/* Header Section */}
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-4">
          <div className="panel-heading border-0">
            <h4 className="panel-title pull-left">Hourly Reports</h4>
          </div>

          <div className="me-3 mt-2">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div className="mt-3">
            <style>
              {`
              .skeleton-text {
                height: 12px;
                background: #ececec;
                background-image: linear-gradient(
                  to right,
                  #ececec 0%,
                  #f4f4f4 20%,
                  #ececec 40%,
                  #ececec 100%
                );
                background-repeat: no-repeat;
                background-size: 800px 100%;
                display: inline-block;
                position: relative;
                animation: skeleton-animation 1.2s ease-in-out infinite;
                border-radius: 4px;
              }

              @keyframes skeleton-animation {
                0% {
                  background-position: -400px 0;
                }
                100% {
                  background-position: 400px 0;
                }
              }
            `}
            </style>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="report-card-container">
                <div className="report-row">
                  <div className="report-content">
                    <div className="skeleton-text" style={{ width: "60%", marginBottom: "10px" }}></div>
                    <div className="skeleton-text" style={{ width: "90%", marginBottom: "6px" }}></div>
                    <div className="skeleton-text" style={{ width: "85%", marginBottom: "6px" }}></div>
                    <div className="skeleton-text" style={{ width: "70%" }}></div>
                  </div>

                  <div className="report-action">
                    <div
                      className="skeleton-text"
                      style={{
                        width: "30px",
                        height: "30px",
                        borderRadius: "50%",
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : hourlyReports.length > 0 ? (
          <div className="mt-3">
            {hourlyReports.map((post, index) => {
              const marketReportHTML = formatMarketReport(post.market_report);

              return (
                <div key={post.id || index} className="report-card-container">
                  <div className="report-row">
                    <div className="report-content">
                      <div
                        style={{ wordWrap: "break-word", whiteSpace: "normal" }}
                        dangerouslySetInnerHTML={{ __html: marketReportHTML }}
                      />
                    </div>

                    <div className="report-action">
                      <button
                        className="btn-share-custom"
                        onClick={() => handleShare(post.id)}
                        title="Share Report"
                      >
                        <FontAwesomeIcon icon={faShare} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>No records found</div>
        )}
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default HourlyReport;
